'use client';

import React from 'react';
import Link from 'next/link';
import { cn } from '@/lib/cn';
import { SpriteIcon } from '@/components/ui/IconSprite';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  /**
   * Click handler — used for client-side state pagination (no URL change).
   * If `basePath` is provided, this is ignored and pagination renders as
   * real <Link> elements (better for SEO).
   */
  onPageChange?: (page: number) => void;
  /**
   * Path used to build pagination hrefs. When provided, pagination renders
   * Next.js <Link> elements so crawlers can follow them and each page is a
   * distinct, indexable URL. Page 1 → `basePath` (no query), pages 2+ →
   * `basePath?page=N` (or `&page=N` if basePath already has a query).
   * String prop (not a function) so it can be passed from a Server
   * Component to this Client Component.
   */
  basePath?: string;
  className?: string;
}

function buildPageHref(basePath: string, page: number): string {
  if (page <= 1) return basePath;
  const sep = basePath.includes('?') ? '&' : '?';
  return `${basePath}${sep}page=${page}`;
}

interface PaginationControlProps {
  type: 'previous' | 'next';
  disabled?: boolean;
  // `| undefined` is required so callers can pass `undefined` explicitly under
  // `exactOptionalPropertyTypes: true` (e.g. when toggling between button-mode
  // and link-mode).
  onClick?: (() => void) | undefined;
  href?: string | undefined;
  className?: string;
}

function PaginationControl({
  type,
  disabled = false,
  onClick,
  href,
  className,
}: PaginationControlProps) {
  const iconName = type === 'previous' ? 'chevron-left' : 'chevron-right';
  const styles = cn(
    'flex items-center justify-center p-2 rounded-full size-8',
    disabled ? 'cursor-not-allowed' : 'bg-primary hover:bg-primary-hover cursor-pointer',
    className,
  );
  const iconClass = cn('size-3', disabled ? 'text-tertiary' : 'text-primary');
  const ariaLabel = `Go to ${type} page`;

  if (href && !disabled) {
    return (
      <Link href={href} className={styles} aria-label={ariaLabel}>
        <SpriteIcon name={iconName} className={iconClass} />
      </Link>
    );
  }
  return (
    <button onClick={onClick} disabled={disabled} className={styles} aria-label={ariaLabel}>
      <SpriteIcon name={iconName} className={iconClass} />
    </button>
  );
}

interface PaginationLinkProps {
  page: number;
  isActive?: boolean;
  // See note on PaginationControlProps.
  onClick?: ((page: number) => void) | undefined;
  href?: string | undefined;
  className?: string;
}

function PaginationLink({ page, isActive = false, onClick, href, className }: PaginationLinkProps) {
  const styles = cn(
    'flex items-center justify-center rounded-full size-8 text-caption font-semibold',
    isActive ? 'bg-accent text-inverted' : 'bg-primary text-primary hover:bg-primary-hover',
    className,
  );
  const ariaLabel = `Go to page ${page}`;
  const ariaCurrent = isActive ? ('page' as const) : undefined;

  if (href) {
    return (
      <Link href={href} className={styles} aria-label={ariaLabel} aria-current={ariaCurrent}>
        {page}
      </Link>
    );
  }
  return (
    <button
      onClick={() => onClick?.(page)}
      className={styles}
      aria-label={ariaLabel}
      aria-current={ariaCurrent}
    >
      {page}
    </button>
  );
}

function PaginationDots({ className }: { className?: string }) {
  return (
    <div
      className={cn('flex items-center justify-center rounded-full size-8 bg-primary', className)}
      aria-hidden="true"
    >
      <span className="text-caption font-semibold text-primary">...</span>
    </div>
  );
}

export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  basePath,
  className,
}: PaginationProps) {
  if (totalPages <= 1) return null;
  const useLinks = typeof basePath === 'string';

  const generatePageNumbers = () => {
    const pages: (number | 'dots')[] = [];

    if (totalPages <= 7) {
      // Show all pages if 7 or fewer
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage <= 4) {
        // Show pages 2, 3, 4, dots, last page
        for (let i = 2; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('dots');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        // Show first page, dots, last 4 pages
        pages.push('dots');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Show first page, dots, current-1, current, current+1, dots, last page
        pages.push('dots');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('dots');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const pageNumbers = generatePageNumbers();

  return (
    <div
      className={cn('flex items-center justify-center gap-2 py-8', className)}
      role="navigation"
      aria-label="Pagination"
    >
      <PaginationControl
        type="previous"
        disabled={currentPage === 1}
        onClick={onPageChange ? () => onPageChange(currentPage - 1) : undefined}
        href={useLinks && currentPage > 1 ? buildPageHref(basePath, currentPage - 1) : undefined}
      />

      {pageNumbers.map((page, index) =>
        page === 'dots' ? (
          <PaginationDots key={`dots-${index}`} />
        ) : (
          <PaginationLink
            key={page}
            page={page}
            isActive={page === currentPage}
            onClick={onPageChange}
            href={useLinks ? buildPageHref(basePath, page) : undefined}
          />
        ),
      )}

      <PaginationControl
        type="next"
        disabled={currentPage === totalPages}
        onClick={onPageChange ? () => onPageChange(currentPage + 1) : undefined}
        href={
          useLinks && currentPage < totalPages
            ? buildPageHref(basePath, currentPage + 1)
            : undefined
        }
      />
    </div>
  );
}
